import { Box, Card, CardContent, Link } from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { jobActions } from '~/thunks/job/jobSlice';
import { dashboardActions } from '~/thunks/dashboard/dashboardSlice';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { IJob, IPayloadDeleteJobs } from '~/utils/interface/job';
import jobApi from '~/apis/job';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { handleGetJob } from '~/thunks/job/jobThunk';
import { handleGetJobs } from '~/thunks/dashboard/dashboardThunk';
import { DIALOG_MODE_EDIT } from '~/utils/constants/common';
import EditJobDialog from '~/components/jobs/edit-job-dialog/EditJobDialog';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import {
  MASTER_DATA_ADDITIONAL_CONTACT_TYPES,
  MASTER_DATA_JOB_CATEGORIES,
  MASTER_DATA_JOB_COST_SUB_TYPES,
  MASTER_DATA_JOB_COST_TYPES,
  MASTER_DATA_JOB_TYPES,
  MASTER_DATA_CUSTOMERS,
  MASTER_DATA_ACCESS_CODE,
  MASTER_DATA_ALL_CUSTOMERS,
  MASTER_DATA_CUSTOMER_STATUSES,
  MASTER_DATA_HAZARD,
  MASTER_DATA_JOB_PROPERTIES,
  MASTER_DATA_PRICES,
  MASTER_DATA_REFERRAL_SOURCES,
  MASTER_DATA_SOURCE_OF_LOSSES,
  MASTER_DATA_WATER_CATEGORY,
  MASTER_DATA_WATER_CLASS,
  MASTER_DATA_CUSTOMER_TYPE,
} from '~/utils/constants/master';
import { adminRouteAbsolute } from '~/utils/constants/route';
import JobList from '~/components/dashboard/job-list/JobList';
import { useNavigate } from 'react-router-dom';

const ProjectSummary = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin/dashboard', 'admin/job', 'admin/component']);
  //#endregion Declare Hook

  //#region Selector
  const { filter, needRefreshData } = useAppSelector((state) => state.dashboard);
  const { selectedIds, openAlertDelete, menuSelectedId } = useAppSelector((state) => state.job);
  //#endregion Selector

  //#region Declare State
  const [openEditJobDialog, setOpenEditJobDialog] = useState<boolean>(false);
  const [editJobDialogMode, setEditJobDialogMode] = useState<string>('');
  const [editData, setEditData] = useState<IJob>();
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    dispatch(handleGetJobs(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filter, needRefreshData]);
  //#endregion Implement Hook

  //#region Handle Function
  const closeAlertDeleteModal = useCallback(() => {
    dispatch(jobActions.changeMenuSelectId(undefined));
    dispatch(jobActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedJobs = useCallback(async () => {
    let payloadDeleteJobs: IPayloadDeleteJobs =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await jobApi.deleteJobs(payloadDeleteJobs);

      notify('success', t('deleteSuccessMessage', { ns: 'admin/job' }));

      dispatch(dashboardActions.changeNeedRefreshData(true));
      dispatch(jobActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  const fetchMasterData = async () => {
    try {
      await dispatch(
        handleGetMasterData({
          type: [
            MASTER_DATA_JOB_TYPES,
            MASTER_DATA_JOB_CATEGORIES,
            MASTER_DATA_JOB_COST_TYPES,
            MASTER_DATA_CUSTOMER_TYPE,
            MASTER_DATA_JOB_COST_SUB_TYPES,
            MASTER_DATA_ADDITIONAL_CONTACT_TYPES,
            MASTER_DATA_CUSTOMERS,
            MASTER_DATA_PRICES,
            MASTER_DATA_ALL_CUSTOMERS,
            MASTER_DATA_REFERRAL_SOURCES,
            MASTER_DATA_JOB_PROPERTIES,
            MASTER_DATA_CUSTOMER_STATUSES,
            MASTER_DATA_SOURCE_OF_LOSSES,
            MASTER_DATA_WATER_CATEGORY,
            MASTER_DATA_WATER_CLASS,
            MASTER_DATA_HAZARD,
            MASTER_DATA_ACCESS_CODE,
          ],
        })
      ).unwrap();
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleOpenEdit = async (id: string) => {
    try {
      await fetchMasterData();
      const response = await dispatch(handleGetJob(id)).unwrap();
      setEditData(response);
      setEditJobDialogMode(DIALOG_MODE_EDIT);
      setOpenEditJobDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setOpenEditJobDialog(open);
    if (needRefreshData) dispatch(dashboardActions.changeNeedRefreshData(true));
  };
  //#endregion Handle Function

  return (
    <>
      <Box className='flex justify-between mb-[16px]'>
        <Box className='text-[#040C2C] font-[500] text-[18px] leading-[26px]'>{t('projectSummary')}</Box>
        <Box>
          <Link
            onClick={() => navigate(adminRouteAbsolute.jobs)}
            className='hover:text-[#007AFC] font-[500]  leading-[26px]'
          >
            {t('viewAll')}
          </Link>
        </Box>
      </Box>
      <Card className='shadow-none border-[#E5EAEF] border rounded-[8px]'>
        <CardContent>
          <JobList onOpenEdit={handleOpenEdit} />
        </CardContent>
      </Card>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription', { ns: 'admin/job' })}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedJobs}
      />
      <EditJobDialog open={openEditJobDialog} setOpen={handleSetOpen} mode={editJobDialogMode} data={editData} />
    </>
  );
};

export default memo(ProjectSummary);
